import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import type { OralExamLoungeHint } from '@/student/module/utils/oralExamLounge';

/** The seven ordered stops of the unified oral-exam Lobby (A1: orientation before waiting). */
export type LobbyStepKey =
  | 'device'
  | 'consent'
  | 'conduct'
  | 'screenShare'
  | 'identity'
  | 'orientation'
  | 'waiting';

export type LobbyStepStatus = 'done' | 'active' | 'upcoming' | 'attention';

/** A resolved step row for the stepper rail. */
export type LobbyStepDescriptor = {
  key: LobbyStepKey;
  label: string;
  status: LobbyStepStatus;
};

/**
 * Action surface the shell forwards to every step so R2/R3/R4 can fill only
 * their own step body without editing the shell. Steps that need none ignore it.
 */
export type LobbyStepActions = {
  recordConsent: () => void;
  acknowledgeConduct: () => void;
  submitIdentityPhotos: (documentImageBase64: string, selfieImageBase64: string) => Promise<void>;
  isRecordingConsent: boolean;
  isAcknowledgingConduct: boolean;
  isSubmittingIdentityPhotos: boolean;
};

/**
 * Shared contract every Lobby step implements. The shell owns the state machine
 * and advances when a step calls `onComplete`. `onBlocked` lets a step report it
 * cannot complete yet (e.g. R3 screen-share not published).
 */
export type LobbyStepProps = {
  runState: OralExamRunStateResponse;
  onComplete: () => void;
  onBlocked?: (reason?: string) => void;
  /** Leave the lobby — surfaced on integrity gates such as mic deny. */
  onLeave?: () => void;
  actions: LobbyStepActions;
  /** Cohort waiting lounge (beat A/B). Ignored outside the waiting step. */
  loungeHints?: OralExamLoungeHint[];
  /** 1:N waiting uses the product answering deck when tenant hints are empty. */
  useProductDefaultHints?: boolean;
  /** undefined = lounge GET in flight; null = confirmed no video; string = SAS URL. */
  orientationVideoUrl?: string | null;
  /** True only after a successful lounge GET. Failures stay unresolved so we do not auto-stamp a missing film. */
  loungeOrientationResolved?: boolean;
  /** True when the session-scoped lounge GET failed (stay on orientation; do not treat as no film). */
  loungeFetchFailed?: boolean;
  onOrientationComplete?: () => void | Promise<void>;
  isCompletingOrientation?: boolean;
  /** True while waiting between orientation stamp retries after a 5xx. */
  orientationStampRetrying?: boolean;
  /** True after three failed stamp attempts — offer continue-anyway. */
  orientationStampExhausted?: boolean;
  onOrientationContinueAnyway?: () => void;
  slotStartIso?: string | null;
  /** A1-S2: reject during orientation film — latch keeps the student on this step. */
  orientationRejectLatch?: boolean;
  orientationRejectChoice?: 'fixNow' | 'finishFirst' | null;
  orientationPausedAtSeconds?: number | null;
  onOrientationRejectFixNow?: () => void;
  onOrientationRejectFinishFirst?: () => void;
  onOrientationPauseOffset?: (seconds: number) => void;
  /** Self-service exams: student starts from the waiting step (never group briefing). */
  onStartPractice?: () => void | Promise<void>;
  isStartingPractice?: boolean;
  startPracticeError?: string | null;
};

/** Canonical, ordered step list. Labels are i18n keys resolved by the shell. */
export const LOBBY_STEPS: ReadonlyArray<{ key: LobbyStepKey; labelKey: string }> = [
  { key: 'consent', labelKey: 'oralExam.lobby.steps.consent' },
  { key: 'conduct', labelKey: 'oralExam.lobby.steps.conduct' },
  { key: 'screenShare', labelKey: 'oralExam.lobby.steps.screenShare' },
  { key: 'device', labelKey: 'oralExam.lobby.steps.device' },
  { key: 'identity', labelKey: 'oralExam.lobby.steps.identity' },
  { key: 'orientation', labelKey: 'oralExam.lobby.steps.orientation' },
  { key: 'waiting', labelKey: 'oralExam.lobby.steps.waiting' },
] as const;
